import { multi, withDependencies } from '@wix/thunderbolt-ioc'
import type { IOnLinkClickHandler } from './types'
import type { BrowserWindow, Experiments, ILinkClickHandler } from '@wix/thunderbolt-symbols'
import {
	BrowserWindowSymbol,
	ExperimentsSymbol,
	NavigationClickHandlerSymbol,
	SiteLinkClickHandlerSymbol,
} from '@wix/thunderbolt-symbols'
import { yieldToMain, applyRetainedQueryParams } from '@wix/thunderbolt-commons'

type HTMLElementTarget = HTMLElement | null

const shouldAllowButtonTagTarget = (eTarget: HTMLElementTarget, experiments: Experiments) =>
	!experiments['specs.thunderbolt.lightboxFromButton'] || (eTarget && eTarget.tagName.toLowerCase() !== 'button')

const getButtonOrAnchorTarget = (event: MouseEvent, experiments: Experiments) => {
	let eTarget = event.target as HTMLElementTarget

	while (
		eTarget &&
		(!eTarget.tagName || (eTarget.tagName.toLowerCase() !== 'a' && shouldAllowButtonTagTarget(eTarget, experiments)))
	) {
		eTarget = eTarget.parentNode as HTMLElementTarget
	}

	return eTarget
}

const shouldNotHandleLink = (anchorTarget: HTMLElementTarget) => {
	if (!anchorTarget) {
		return false
	}
	const href = anchorTarget.getAttribute('href') || ''
	return anchorTarget.getAttribute('data-cancel-link') || (href.startsWith('#') && href !== '#')
}

export const onLinkClickHandler = (
	experiments: Experiments,
	navigationHandler: ILinkClickHandler,
	siteLinkClickHandlers: Array<ILinkClickHandler>,
	_browserWindow?: BrowserWindow
): IOnLinkClickHandler => {
	const masterPageClickHandlers: Array<ILinkClickHandler> = []
	const pageClickHandlers: Array<ILinkClickHandler> = []
	return {
		onLinkClick: async (e: MouseEvent) => {
			let shouldResumeDefault = true
			if (e.metaKey || e.ctrlKey) {
				return
			}
			const anchorTarget = getButtonOrAnchorTarget(e, experiments)

			if (!anchorTarget) {
				return
			}

			const href = anchorTarget.getAttribute('href') || ''
			if (shouldNotHandleLink(anchorTarget)) {
				return
			}

			const isEditable = !!anchorTarget.closest('[contenteditable="true"]')
			const shouldRunSynchronously =
				process.env.PACKAGE_NAME === 'thunderbolt-ds' || href.startsWith('blob:') || isEditable
			if (!shouldRunSynchronously) {
				e.preventDefault()
				await yieldToMain()
			}

			const fireHandlers = (handlers: Array<ILinkClickHandler>) => {
				for (const handler of handlers) {
					const didHandle = handler.handleClick(anchorTarget)
					if (didHandle) {
						shouldResumeDefault = false
						if (shouldRunSynchronously) {
							e.preventDefault()
						}
						e.stopPropagation()
						return
					}
				}
			}

			fireHandlers([...siteLinkClickHandlers, ...masterPageClickHandlers, ...pageClickHandlers, navigationHandler])

			if (!shouldRunSynchronously && shouldResumeDefault) {
				if (window && href) {
					// apply retained query params for links existing the current tab
					const hrefWithInternalQueryParams = applyRetainedQueryParams(new URL(window.location.href), href).href
					window.open(hrefWithInternalQueryParams, anchorTarget.getAttribute('target') || '_self')
				}
			}
		},
		registerPageClickHandler: (handler: ILinkClickHandler, pageId: string) => {
			pageId === 'masterPage' ? masterPageClickHandlers.push(handler) : pageClickHandlers.push(handler)
		},
		removePageClickHandler: (handler: ILinkClickHandler, pageId: string) => {
			const removeHandler = (handlers: Array<ILinkClickHandler>, handlerId?: string) => {
				const index = handlers.findIndex((currentHandler) => currentHandler.handlerId === handlerId)
				if (index !== -1) {
					handlers.splice(index, 1)
				}
			}

			const handlersList = pageId === 'masterPage' ? masterPageClickHandlers : pageClickHandlers
			removeHandler(handlersList, handler.handlerId)
		},
	}
}

export const OnLinkClickHandler = withDependencies(
	[ExperimentsSymbol, NavigationClickHandlerSymbol, multi(SiteLinkClickHandlerSymbol), BrowserWindowSymbol] as const,
	onLinkClickHandler
)
